import { InternalId, IOptionalRuntimeReference } from '@edgebox/data-definition-kit';
import {
  AccessScope,
  TaskDomain,
  TaskGroupType,
  TaxonomyCategory,
  TaxonomyFacet,
  TaxonomyStatus,
} from '@edgebox/sync-core-data-definitions';
import { ClientCustomerEntity, ClientProjectEntity, ClientTaxonomyDraft, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { CATEGORY_ORDER } from './GovernanceTaxonomyManagement.utils';
import {
  FACET_METADATA,
  IFacetMetadata,
  facetCreationDefaults,
  PRODUCT_ATTRIBUTE_REQUIRED_WHEN,
  REQUIRED_PROJECT_FACETS,
} from './taxonomyFacetMetadata';

export interface ITaxonomyFacetSuggestion {
  facet: TaxonomyFacet;
  /** Raw AI confidence; accepts 0..1 or 0..100 inputs. */
  confidence?: number;
  reason?: string;
}

export type TaxonomySelectionRowState = 'locked' | 'required-missing' | 'existing' | 'selected' | 'recommended' | 'available';

export interface ITaxonomySelectionRow {
  facet: TaxonomyFacet;
  name: string;
  description: string;
  icon: IconDefinition;
  category: TaxonomyCategory;
  state: TaxonomySelectionRowState;
  /** Only set for `recommended` rows when recommendation info should be shown. */
  displayedConfidence?: number;
  reason?: string;
  /** Marks rows the user previously ignored; they stay reachable via "Show all". */
  wasIgnored?: boolean;
}

export interface ITaxonomySelectionGroup {
  category: TaxonomyCategory;
  /** Rows that are always visible: locked, existing, selected, recommended. */
  rows: ITaxonomySelectionRow[];
  /** Rows revealed by "Show all": non-recommended optional facets and ignored ones. */
  showAllRows: ITaxonomySelectionRow[];
  /** False renders the "None recommended." hint for the category. */
  hasVisibleRows: boolean;
}

export const BUSINESS_RECOMMENDED_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.Product,
  TaxonomyFacet.ProductFeature,
  TaxonomyFacet.CompetitorProduct,
  TaxonomyFacet.Market,
  TaxonomyFacet.TargetIndustry,
];

export const HIGHER_ED_RECOMMENDED_FACETS: readonly TaxonomyFacet[] = [
  TaxonomyFacet.AcademicUnit,
  TaxonomyFacet.Program,
  TaxonomyFacet.CompetitorProgram,
  TaxonomyFacet.Credential,
  TaxonomyFacet.AcademicLevel,
];

/**
 * Maps the detected organization type to the facets we recommend for it. Anything that is
 * not clearly higher education (including missing or failed extraction) falls back to the
 * business list; the AI facet relevance still surfaces other facets on top.
 */
export function recommendedFacetsForOrganizationType(organizationType: string | undefined | null): readonly TaxonomyFacet[] {
  const normalized = String(organizationType || '')
    .toLowerCase()
    .replace(/[^a-z]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (
    normalized.includes('higher ed') ||
    normalized.includes('higher education') ||
    normalized === 'university' ||
    normalized === 'college'
  ) {
    return HIGHER_ED_RECOMMENDED_FACETS;
  }

  return BUSINESS_RECOMMENDED_FACETS;
}

/** Normalizes raw AI confidence (0..1 or 0..100) to an integer percentage 0..100. */
export function normalizeConfidence(confidence: unknown): number {
  if (typeof confidence !== 'number' || Number.isNaN(confidence) || !Number.isFinite(confidence)) {
    return 0;
  }

  const value = confidence >= 0 && confidence <= 1 ? confidence * 100 : confidence;
  return Math.max(0, Math.min(100, Math.round(value)));
}

/**
 * Confidence displayed for facets recommended by organization type:
 * 90% base + 10% × the AI's own confidence. A facet the AI did not score shows 90%.
 */
export function displayedConfidenceForOrgTypeFacet(rawConfidence: unknown): number {
  return Math.round(90 + 10 * (normalizeConfidence(rawConfidence) / 100));
}

function normalizeFacetToken(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Parses the `taxonomyFacetRelevance` part of the onboarding main-page extraction into a
 * plain suggestion list. Facets without metadata (unknown or custom) are dropped.
 */
export function parseFacetSuggestions(extraction: {
  taxonomyFacetRelevance?: {
    parsed?: { facets?: unknown[] } | null;
    terms?: Array<{ name?: string | null; confidence?: number | null; reasoning?: string | null }> | null;
  } | null;
}): ITaxonomyFacetSuggestion[] {
  const parsedFacets = extraction.taxonomyFacetRelevance?.parsed?.facets;
  if (!Array.isArray(parsedFacets)) {
    return [];
  }

  const terms = extraction.taxonomyFacetRelevance?.terms || [];
  const termByFacet = new Map<string, { confidence?: number; reason?: string }>();

  for (const term of terms) {
    const name = String(term?.name || '').trim();
    if (!name) {
      continue;
    }

    termByFacet.set(normalizeFacetToken(name), {
      confidence: typeof term?.confidence === 'number' ? term.confidence : undefined,
      reason: term?.reasoning || undefined,
    });
  }

  const seen = new Set<string>();
  const suggestions: ITaxonomyFacetSuggestion[] = [];

  for (const rawFacet of parsedFacets) {
    const facet = String(rawFacet || '').trim();
    if (!facet || seen.has(facet) || !FACET_METADATA.has(facet as TaxonomyFacet)) {
      continue;
    }

    seen.add(facet);
    const termMetadata = termByFacet.get(normalizeFacetToken(facet));
    suggestions.push({
      facet: facet as TaxonomyFacet,
      confidence: termMetadata?.confidence,
      reason: termMetadata?.reason,
    });
  }

  return suggestions;
}

/**
 * The byte-sized recommendation payload consumed by the selection UIs. Components render
 * it as-is; how it is obtained (onboarding extraction, host page, …) is the host's concern.
 */
export interface ITaxonomyRecommendations {
  aiSuggestions: ITaxonomyFacetSuggestion[];
  organizationTypeFacets: readonly TaxonomyFacet[];
}

/** Derives the recommendation payload from an onboarding main-page extraction result. */
export function extractTaxonomyRecommendations(extraction: {
  taxonomyFacetRelevance?: Parameters<typeof parseFacetSuggestions>[0]['taxonomyFacetRelevance'];
  organizationTypeSegmentation?: { parsed?: unknown } | null;
}): ITaxonomyRecommendations {
  const organizationType = (extraction.organizationTypeSegmentation?.parsed as { organizationType?: unknown } | null | undefined)
    ?.organizationType;

  return {
    aiSuggestions: parseFacetSuggestions(extraction),
    organizationTypeFacets: recommendedFacetsForOrganizationType(typeof organizationType === 'string' ? organizationType : undefined),
  };
}

/**
 * Best-effort lookup of taxonomy recommendations from the latest governance onboarding.
 * Returns undefined when no onboarding data is resolvable so callers can render without
 * recommendation info.
 */
export async function loadOnboardingTaxonomyRecommendations(api: ISyncCoreApi): Promise<ITaxonomyRecommendations | undefined> {
  try {
    const taskGroupList = await api.service.taskGroups.list(
      {
        domains: [TaskDomain.Governance],
        types: [TaskGroupType.Onboarding],
      },
      {
        page: 0,
        itemsPerPage: 20,
      }
    );

    const latestTaskGroup = [...(taskGroupList.items || [])].sort((a, b) => String(b.id).localeCompare(String(a.id)))[0];
    if (!latestTaskGroup?.id) {
      return undefined;
    }

    const extraction = await api.governance.onboarding.mainPageDataExtraction({
      taskGroupId: latestTaskGroup.id as InternalId,
    });

    return extractTaxonomyRecommendations(extraction);
  } catch {
    return undefined;
  }
}

export interface IBuildTaxonomySelectionGroupsInput {
  /** Facets of taxonomies that already exist in the project. */
  existingFacets: Iterable<TaxonomyFacet>;
  /** AI facet suggestions from the onboarding extraction (may be empty). */
  aiSuggestions: ITaxonomyFacetSuggestion[];
  /** Facets recommended for the detected organization type. */
  organizationTypeFacets: readonly TaxonomyFacet[];
  /** Facets the user accepted in this session. */
  selectedFacets: Iterable<TaxonomyFacet>;
  /** Facets the user X'd in this session; they move to the "Show all" section. */
  ignoredFacets: Iterable<TaxonomyFacet>;
  /** When false, hides confidence/reason (settings modal without onboarding data). */
  showRecommendationInfo: boolean;
  /**
   * When true, a required facet that does not yet exist renders as `required-missing`
   * (an addable row) instead of `locked`, so it can be created for an existing project.
   * Onboarding leaves this false: required facets are created together on continue, so
   * they stay locked. Existing required facets always render as `existing`, never here.
   */
  allowAddingRequired?: boolean;
}

/** Whether `product-attribute` should lock in given the facets already in play. */
export function isProductAttributeRequired(activeFacets: Iterable<TaxonomyFacet>): boolean {
  const active = activeFacets instanceof Set ? activeFacets : new Set(activeFacets);
  return PRODUCT_ATTRIBUTE_REQUIRED_WHEN.some((facet) => active.has(facet));
}

function toRow(
  facet: TaxonomyFacet,
  metadata: IFacetMetadata,
  state: TaxonomySelectionRowState,
  extra?: Partial<Pick<ITaxonomySelectionRow, 'displayedConfidence' | 'reason' | 'wasIgnored'>>
): ITaxonomySelectionRow {
  return {
    facet,
    name: metadata.name,
    description: metadata.description,
    icon: metadata.icon,
    category: metadata.category,
    state,
    ...extra,
  };
}

const PRIMARY_STATE_ORDER: Record<TaxonomySelectionRowState, number> = {
  locked: 0,
  'required-missing': 1,
  existing: 2,
  selected: 3,
  recommended: 4,
  available: 5,
};

/**
 * Builds the grouped row model for the taxonomy selection UI. System facets are never
 * included; required facets render as "existing" when already created, otherwise locked
 * (onboarding) or "required-missing"/addable (management, via `allowAddingRequired`);
 * ignored recommendations stay reachable in the "Show all" section.
 */
export function buildTaxonomySelectionGroups(input: IBuildTaxonomySelectionGroupsInput): ITaxonomySelectionGroup[] {
  const existing = new Set(input.existingFacets);
  const selected = new Set(input.selectedFacets);
  const ignored = new Set(input.ignoredFacets);
  const suggestionByFacet = new Map(input.aiSuggestions.map((suggestion) => [suggestion.facet, suggestion]));
  const orgTypeSet = new Set(input.organizationTypeFacets);

  const rowsByCategory = new Map<TaxonomyCategory, { rows: ITaxonomySelectionRow[]; showAllRows: ITaxonomySelectionRow[] }>();

  const pushRow = (row: ITaxonomySelectionRow, section: 'rows' | 'showAllRows') => {
    const bucket = rowsByCategory.get(row.category) || { rows: [], showAllRows: [] };
    bucket[section].push(row);
    rowsByCategory.set(row.category, bucket);
  };

  // `product-attribute` locks in when products are already tracked or selected this session.
  const productAttributeRequired = isProductAttributeRequired([...existing, ...selected]);

  for (const [facet, metadata] of FACET_METADATA.entries()) {
    if (metadata.scope === 'system') {
      continue;
    }

    if (existing.has(facet)) {
      pushRow(toRow(facet, metadata, 'existing'), 'rows');
      continue;
    }

    const isRequired = metadata.scope === 'required' || (facet === TaxonomyFacet.ProductAttribute && productAttributeRequired);
    if (isRequired) {
      // A required facet that is missing: keep it addable in management (so it can be
      // created for an existing project) but locked in onboarding (created on continue).
      if (selected.has(facet)) {
        pushRow(toRow(facet, metadata, 'selected'), 'rows');
      } else {
        pushRow(toRow(facet, metadata, input.allowAddingRequired ? 'required-missing' : 'locked'), 'rows');
      }
      continue;
    }

    if (selected.has(facet)) {
      pushRow(toRow(facet, metadata, 'selected'), 'rows');
      continue;
    }

    const suggestion = suggestionByFacet.get(facet);
    const isOrgTypeRecommended = orgTypeSet.has(facet);
    const isRecommended = isOrgTypeRecommended || !!suggestion;

    if (isRecommended && !ignored.has(facet)) {
      const displayedConfidence = isOrgTypeRecommended
        ? displayedConfidenceForOrgTypeFacet(suggestion?.confidence)
        : normalizeConfidence(suggestion?.confidence);

      pushRow(
        toRow(facet, metadata, 'recommended', {
          displayedConfidence: input.showRecommendationInfo ? displayedConfidence : undefined,
          reason: input.showRecommendationInfo ? suggestion?.reason : undefined,
        }),
        'rows'
      );
      continue;
    }

    pushRow(toRow(facet, metadata, 'available', { wasIgnored: ignored.has(facet) || undefined }), 'showAllRows');
  }

  const groups: ITaxonomySelectionGroup[] = [];

  for (const category of CATEGORY_ORDER) {
    const bucket = rowsByCategory.get(category);
    if (!bucket || (!bucket.rows.length && !bucket.showAllRows.length)) {
      continue;
    }

    bucket.rows.sort(
      (a, b) =>
        PRIMARY_STATE_ORDER[a.state] - PRIMARY_STATE_ORDER[b.state] ||
        (b.displayedConfidence || 0) - (a.displayedConfidence || 0) ||
        a.name.localeCompare(b.name)
    );
    bucket.showAllRows.sort((a, b) => a.name.localeCompare(b.name));

    groups.push({
      category,
      rows: bucket.rows,
      showAllRows: bucket.showAllRows,
      hasVisibleRows: bucket.rows.length > 0,
    });
  }

  return groups;
}

export interface ICreateTaxonomiesForFacetsOptions {
  api: ISyncCoreApi;
  /** Facets to create in addition to the required ones. */
  facets: Iterable<TaxonomyFacet>;
  projectId: InternalId;
  customer?: IOptionalRuntimeReference<ClientCustomerEntity>;
  project?: IOptionalRuntimeReference<ClientProjectEntity>;
}

/**
 * Idempotently creates project taxonomies for the requested facets plus all required
 * facets: re-lists the project's taxonomies and skips facets that already exist or have
 * no creation defaults (system facets).
 */
export async function createTaxonomiesForFacets(options: ICreateTaxonomiesForFacetsOptions): Promise<void> {
  const existingProjectTaxonomies = await options.api.service.taxonomies.list(
    { page: 0, itemsPerPage: 500 },
    { projectId: options.projectId }
  );

  const existingFacets = new Set<TaxonomyFacet>();
  for (const taxonomy of existingProjectTaxonomies.items || []) {
    if (taxonomy.facet) {
      existingFacets.add(taxonomy.facet as TaxonomyFacet);
    }
  }

  const facetsToCreate = new Set<TaxonomyFacet>([...REQUIRED_PROJECT_FACETS, ...options.facets]);

  // organization-attribute is required (seeded via REQUIRED_PROJECT_FACETS); product-attribute
  // couples to products: seed it too once Product or Competitor product is in play.
  if (isProductAttributeRequired([...existingFacets, ...facetsToCreate])) {
    facetsToCreate.add(TaxonomyFacet.ProductAttribute);
  }

  for (const facet of facetsToCreate) {
    if (existingFacets.has(facet)) {
      continue;
    }

    const settings = facetCreationDefaults(facet);
    if (!settings) {
      continue;
    }

    const draft = new ClientTaxonomyDraft();
    draft.accessScope = AccessScope.Project;
    draft.status = TaxonomyStatus.Active;
    draft.facet = facet;
    draft.name = settings.name;
    draft.category = settings.category;
    draft.kind = settings.kind;
    draft.allowedOrigins = [...settings.allowedOrigins];
    draft.allowedTargets = [...settings.allowedTargets];
    draft.isTimeBased = settings.isTimeBased;
    draft.allowAddingTerms = settings.allowAddingTerms;
    draft.autoClassifyContent = settings.autoClassifyContent;
    draft.allowedCollectionTaxonomies = [];

    if (options.customer) {
      draft.customer = options.customer;
    }

    if (options.project) {
      draft.project = options.project;
    }

    await options.api.service.taxonomies.create(draft);
    existingFacets.add(facet);
  }
}
